import { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { Trash2, Upload, Download, Plus, Paperclip } from 'lucide-react'

import { Button } from '@/components/newRedesign/primitives/Button'
import { ActionIconButton } from '@/components/newRedesign/components/ActionIconButton'
import { FormTextarea } from '@/components/newRedesign/form/FormTextarea'
import { DataTable } from '@/components/newRedesign/components/DataTable'
import { Input } from '@/components/newRedesign/primitives/Input'
import { cn } from '@/lib/utils'

import { ContainerDocument } from '../CreateContainerWizard'

interface DocumentsStepProps {
  documents: ContainerDocument[]
  onDocumentsChange: (_documents: ContainerDocument[]) => void
  onUploadModeChange?: (_isUploading: boolean) => void
  onEditModeChange?: (_isEditing: boolean) => void
  onPendingDocumentsCountChange?: (_count: number) => void
  onFormRefChange?: (
    _ref: { submit: () => void; cancel: () => void } | null
  ) => void
}

interface DocumentFormData {
  observations: string
}

interface EditDocumentFormData {
  name: string
  observations: string
}

interface PendingDocument {
  id: string
  name: string
  file: File
  size: string
}

export function DocumentsStep({
  documents,
  onDocumentsChange,
  onUploadModeChange,
  onEditModeChange,
  onPendingDocumentsCountChange,
  onFormRefChange,
}: DocumentsStepProps) {
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingDocument, setEditingDocument] =
    useState<ContainerDocument | null>(null)
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>(
    []
  )
  const [dragActive, setDragActive] = useState(false)
  const [editingDocId, setEditingDocId] = useState<string | null>(null)

  const { control, handleSubmit, reset } = useForm<DocumentFormData>({
    defaultValues: {
      observations: '',
    },
  })

  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
  } = useForm<EditDocumentFormData>({
    defaultValues: {
      name: '',
      observations: '',
    },
  })

  const handleCancelUpload = useCallback(() => {
    setShowUploadForm(false)
    setPendingDocuments([])
    onUploadModeChange?.(false)
    onPendingDocumentsCountChange?.(0)
    reset()
  }, [onUploadModeChange, onPendingDocumentsCountChange, reset])

  // Update pending documents count whenever it changes
  useEffect(() => {
    onPendingDocumentsCountChange?.(pendingDocuments.length)
  }, [pendingDocuments.length, onPendingDocumentsCountChange])

  const handleStartUpload = useCallback(() => {
    setShowUploadForm(true)
    onUploadModeChange?.(true)
  }, [onUploadModeChange])

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const handleFilesSelect = (files: FileList | null) => {
    if (!files) return

    const newPendingDocs: PendingDocument[] = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      name: file.name,
      file: file,
      size: formatFileSize(file.size),
    }))

    setPendingDocuments((prev) => [...prev, ...newPendingDocs])
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(true)
  }

  const handleDragLeave = () => {
    setDragActive(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(false)
    handleFilesSelect(e.dataTransfer.files)
  }

  const handleRemovePendingDocument = (id: string) => {
    setPendingDocuments((prev) => prev.filter((doc) => doc.id !== id))
  }

  const handleEditPendingDocumentName = (id: string, newName: string) => {
    setPendingDocuments((prev) =>
      prev.map((doc) => (doc.id === id ? { ...doc, name: newName } : doc))
    )
  }

  const handleAddDocuments = useCallback(
    (data: DocumentFormData) => {
      if (pendingDocuments.length === 0) {
        return
      }

      const newDocuments: ContainerDocument[] = pendingDocuments.map((doc) => ({
        id: doc.id,
        name: doc.name,
        observations: data.observations,
        file: doc.file,
      }))

      onDocumentsChange([...documents, ...newDocuments])
      setShowUploadForm(false)
      setPendingDocuments([])
      onUploadModeChange?.(false)
      onPendingDocumentsCountChange?.(0)
      reset()
    },
    [
      pendingDocuments,
      documents,
      onDocumentsChange,
      onUploadModeChange,
      onPendingDocumentsCountChange,
      reset,
    ]
  )

  const handleStartEditDocument = useCallback(
    (doc: ContainerDocument) => {
      const fullName = doc.name || ''

      if (fullName.startsWith('.')) {
        setEditingDocument({
          ...doc,
          name: '',
          originalExtension: fullName,
        } as ContainerDocument & { originalExtension: string })
        resetEdit({
          name: '',
          observations: doc.observations || '',
        })
      } else {
        const lastDotIndex = fullName.lastIndexOf('.')
        const hasExtension = lastDotIndex !== -1 && lastDotIndex > 0
        const nameWithoutExtension = hasExtension
          ? fullName.substring(0, lastDotIndex)
          : fullName
        const extension = hasExtension ? fullName.substring(lastDotIndex) : ''

        setEditingDocument({
          ...doc,
          originalExtension: extension,
        } as ContainerDocument & { originalExtension: string })
        resetEdit({
          name: nameWithoutExtension,
          observations: doc.observations || '',
        })
      }

      setShowEditForm(true)
      onEditModeChange?.(true)
    },
    [onEditModeChange, resetEdit]
  )

  const handleCancelEdit = useCallback(() => {
    setShowEditForm(false)
    setEditingDocument(null)
    onEditModeChange?.(false)
    resetEdit()
  }, [onEditModeChange, resetEdit])

  const handleSaveEditDocument = useCallback(
    (data: EditDocumentFormData) => {
      if (!editingDocument) return

      // Agregar la extensión original al nombre antes de guardar
      const docWithExtension = editingDocument as ContainerDocument & {
        originalExtension?: string
      }
      const extension = docWithExtension.originalExtension || ''
      const newName = data.name?.trim() || ''
      const fullName = newName + extension

      const updatedDocuments = documents.map((doc) =>
        doc.id === editingDocument.id
          ? {
              ...doc,
              name: fullName,
              observations: data.observations,
            }
          : doc
      )

      onDocumentsChange(updatedDocuments)
      setShowEditForm(false)
      setEditingDocument(null)
      onEditModeChange?.(false)
      resetEdit()
    },
    [editingDocument, documents, onDocumentsChange, onEditModeChange, resetEdit]
  )

  // Expose submit and cancel functions to parent
  useEffect(() => {
    if (showUploadForm) {
      onFormRefChange?.({
        submit: () => handleSubmit(handleAddDocuments)(),
        cancel: handleCancelUpload,
      })
    } else if (showEditForm) {
      onFormRefChange?.({
        submit: () => handleEditSubmit(handleSaveEditDocument)(),
        cancel: handleCancelEdit,
      })
    } else {
      onFormRefChange?.(null)
    }
  }, [
    showUploadForm,
    showEditForm,
    onFormRefChange,
    handleSubmit,
    handleEditSubmit,
    handleAddDocuments,
    handleSaveEditDocument,
    handleCancelUpload,
    handleCancelEdit,
  ])

  const handleDeleteDocument = (id: string) => {
    onDocumentsChange(documents.filter((doc) => doc.id !== id))
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4">
      {!showUploadForm && !showEditForm ? (
        <>
          {/* Add Document Button */}
          <div className="flex shrink-0 justify-start">
            <Button
              className={cn(
                'h-9 min-h-9 gap-2 rounded-md bg-[#1E293B] px-4',
                'text-sm font-medium leading-5 tracking-[0.005em]',
                'text-[#FAFAFA] hover:bg-[#1E293B]/90'
              )}
              onClick={handleStartUpload}
              size="md"
              type="button"
            >
              <Paperclip className="h-4 w-4" />
              Adjuntar documentos
            </Button>
          </div>

          {/* Documents Table */}
          <div className="min-h-0 flex-1 overflow-auto rounded-md border border-[#E5E5E5]">
            <DataTable
              alignColumns={true}
              columns={['Nombre', 'Observaciones', 'Acciones']}
              data={documents}
              emptyMessage="No hay documentos adjuntos"
              onRowClick={(doc) => handleStartEditDocument(doc)}
              renderRow={(doc) => {
                let displayName =
                  doc.name && doc.name.trim() !== '' ? doc.name : '-'
                if (displayName.startsWith('.')) {
                  displayName = '-'
                }

                return (
                  <>
                    <td className="px-3 py-1.5 text-left text-sm">
                      {displayName}
                    </td>
                    <td className="px-3 py-1.5 text-center text-sm">
                      {doc.observations || '-'}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <ActionIconButton
                          className="h-8 w-8 text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                          icon={<Download className="h-4 w-4" />}
                          label="Descargar"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (doc.file) {
                              const url = URL.createObjectURL(doc.file)
                              const fileType = doc.file.type.toLowerCase()

                              // Si es PDF o imagen, intentar abrir en nueva pestaña
                              if (
                                fileType === 'application/pdf' ||
                                fileType.startsWith('image/')
                              ) {
                                window.open(url, '_blank')
                              } else {
                                // Para otros tipos, descargar
                                const a = document.createElement('a')
                                a.href = url
                                a.download = doc.name || doc.file.name
                                document.body.appendChild(a)
                                a.click()
                                document.body.removeChild(a)
                              }

                              // Limpiar el URL después de un tiempo
                              setTimeout(() => URL.revokeObjectURL(url), 100)
                            }
                          }}
                          size="icon"
                          variant="ghost"
                        />
                        <ActionIconButton
                          className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700"
                          icon={<Trash2 className="h-4 w-4" />}
                          label="Eliminar"
                          onClick={(e) => {
                            // Prevenir propagación del evento para que no active el formulario padre
                            e.preventDefault()
                            e.stopPropagation()
                            if (doc.id) {
                              handleDeleteDocument(doc.id)
                            }
                          }}
                          size="icon"
                          variant="ghost"
                        />
                      </div>
                    </td>
                  </>
                )
              }}
            />
          </div>
        </>
      ) : showUploadForm ? (
        <>
          <div className="flex flex-col gap-4">
            <h3 className="text-xl font-semibold leading-[1.2] tracking-[-0.02em] text-[#262626]">
              Nuevo documento
            </h3>

            {pendingDocuments.length === 0 ? (
              <div className="flex flex-col gap-4">
                <div
                  className={cn(
                    'flex cursor-pointer flex-col items-center justify-center',
                    'gap-8 rounded-md border border-dashed p-4 transition-colors',
                    'min-h-[320px]',
                    dragActive
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-[#D4D4D4] bg-transparent'
                  )}
                  onClick={() =>
                    document.getElementById('file-input-multiple')?.click()
                  }
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  {/* Icon */}
                  <div className="flex h-12 w-12 items-center justify-center">
                    <Upload className="h-10 w-10 text-[#737373]" />
                  </div>

                  {/* Text Section */}
                  <div className="flex flex-col items-center gap-1.5">
                    <span className="text-center text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                      Arrastra y suelta un archivo o haz clic para buscar
                    </span>
                    <span className="text-center text-xs font-normal leading-4 tracking-[0.015em] text-[#262626]">
                      Permitidos: PDF, Imágenes (.jpeg, .png, .jpg), Word (DOC,
                      DOCX), Excel (XLS, XLSX)
                    </span>
                  </div>

                  {/* Button */}
                  <Button
                    className={cn(
                      'h-8 min-h-8 min-w-8 gap-2 rounded-md',
                      'border border-[#D4D4D4] bg-[#FAFAFA] px-3',
                      'shadow-[0px_1px_2px_rgba(0,0,0,0.05)]',
                      'hover:bg-[#F5F5F5]'
                    )}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    <span className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                      Seleccionar archivo
                    </span>
                  </Button>

                  <input
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    className="hidden"
                    id="file-input-multiple"
                    multiple
                    onChange={(e) => handleFilesSelect(e.target.files)}
                    type="file"
                  />
                </div>
              </div>
            ) : (
              <div className="flex max-h-[320px] min-h-[270px] flex-col gap-4 lg:min-h-[320px]">
                <div className="flex shrink-0 flex-row flex-wrap items-center gap-2">
                  <Button
                    className={cn(
                      'h-9 min-h-9 min-w-9 gap-2 rounded-md',
                      'bg-[#1E293B] px-4 hover:bg-[#1E293B]/90'
                    )}
                    onClick={() =>
                      document.getElementById('file-input-multiple')?.click()
                    }
                    size="sm"
                    type="button"
                  >
                    <Plus className="h-4 w-4 text-[#FAFAFA]" />
                    <span className="text-sm font-medium leading-5 tracking-[0.005em] text-[#FAFAFA]">
                      Archivo
                    </span>
                  </Button>
                </div>
                <input
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  className="hidden"
                  id="file-input-multiple"
                  multiple
                  onChange={(e) => handleFilesSelect(e.target.files)}
                  type="file"
                />
                <div
                  className={cn(
                    'flex min-h-0 flex-1 flex-col overflow-hidden',
                    'rounded-md border border-[#D4D4D4] bg-[#FAFAFA]'
                  )}
                >
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <DataTable
                      alignColumns={true}
                      columns={['Nombre', 'Tamaño', 'Acciones']}
                      data={pendingDocuments}
                      emptyMessage=""
                      renderRow={(doc) => {
                        const lastDotIndex = doc.name.lastIndexOf('.')
                        const hasExtension = lastDotIndex !== -1
                        const nameWithoutExt = hasExtension
                          ? doc.name.substring(0, lastDotIndex)
                          : doc.name
                        const extension = hasExtension
                          ? doc.name.substring(lastDotIndex)
                          : ''
                        const isEditing = editingDocId === doc.id

                        return (
                          <>
                            <td className="border-b border-neutral-300 px-2 py-2">
                              <Input
                                className={cn(
                                  'h-9 w-full rounded-md border border-[#D4D4D4]',
                                  'bg-[#FAFAFA] px-4 text-sm font-medium',
                                  'leading-5 tracking-[0.005em] text-[#171717]',
                                  'shadow-[0px_1px_2px_rgba(0,0,0,0.05)]'
                                )}
                                onBlur={() => setEditingDocId(null)}
                                onChange={(e) =>
                                  handleEditPendingDocumentName(
                                    doc.id,
                                    isEditing
                                      ? e.target.value + extension
                                      : e.target.value
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                                onFocus={() => setEditingDocId(doc.id)}
                                type="text"
                                value={isEditing ? nameWithoutExt : doc.name}
                              />
                            </td>
                            <td
                              className={cn(
                                'w-24 border-b border-neutral-300 px-3 py-2',
                                'text-left text-sm leading-5',
                                'tracking-[0.005em] text-[#262626]'
                              )}
                            >
                              {doc.size}
                            </td>
                            <td className="border-b border-neutral-300 px-2 py-2 text-right">
                              <div className="flex items-center justify-end">
                                <ActionIconButton
                                  className={cn(
                                    'h-8 w-8 rounded-md',
                                    'text-[#DC2626] hover:bg-red-50'
                                  )}
                                  icon={<Trash2 className="h-4 w-4" />}
                                  label="Eliminar"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleRemovePendingDocument(doc.id)
                                  }}
                                  size="icon"
                                  variant="ghost"
                                />
                              </div>
                            </td>
                          </>
                        )
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex shrink-0 flex-col gap-2">
              <FormTextarea
                control={control}
                disabled={false}
                label="Observaciones"
                name="observations"
                placeholder="Observaciones (aplicarán a todos los documentos)"
                rows={3}
                showLabel={true}
                textareaClassName={cn(
                  'h-20 rounded-lg border border-[#D4D4D4] bg-[#FAFAFA]',
                  'p-2 text-sm leading-5 tracking-[0.005em] text-[#171717]',
                  'shadow-[0px_1px_2px_rgba(0,0,0,0.05)]'
                )}
              />
            </div>
          </div>
        </>
      ) : showEditForm ? (
        <>
          {/* Edit Document Form */}
          <div className="flex flex-col gap-4">
            <h3 className="text-xl font-semibold leading-[1.2] tracking-[-0.02em] text-[#262626]">
              Editar documento
            </h3>

            {/* Name Input */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Nombre
              </label>
              <Input
                className={cn(
                  'h-9 w-full rounded-md border border-[#D4D4D4]',
                  'bg-[#FAFAFA] px-4 text-sm leading-5',
                  'tracking-[0.005em] text-[#171717]',
                  'shadow-[0px_1px_2px_rgba(0,0,0,0.05)]'
                )}
                placeholder="Cambiar el nombre del documento"
                {...editControl.register('name')}
              />
            </div>

            {/* Observations */}
            <div className="flex flex-col gap-2">
              <FormTextarea
                control={editControl}
                label="Observaciones"
                name="observations"
                placeholder="Observaciones"
                rows={4}
                showLabel={true}
                textareaClassName={cn(
                  'h-20 rounded-lg border border-[#D4D4D4] bg-[#FAFAFA]',
                  'p-2 text-sm font-normal leading-5 tracking-[0.005em]',
                  'text-[#171717] shadow-[0px_1px_2px_rgba(0,0,0,0.05)]'
                )}
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
